import endpoints from 'constants/endpoints';
import { safeAWSCallPost } from 'helpers/firebaseSetup';

// bulk onboarding invite helpers (ONEILL-886) — O'Neill only, CSV only.
// the CSV template in S3 is the single source of truth: the admin downloads it
// here and the backend validates uploads against it. the frontend does not
// parse the file — it uploads the raw CSV and the backend validates it.

// public S3 static asset — downloaded directly by the admin (no backend hop).
const ONBOARDING_TEMPLATE_URL =
  'https://oneill-static-assets.s3.us-east-1.amazonaws.com/bulk_onboarding_template.csv';

// each row's outcome category (falls back for older payloads without it).
export function catOf(r) {
  return r.category ?? (r.ok ? 'invited' : 'error');
}

/**
 * triggers a browser download of the onboarding bulk-invite CSV template
 * (a public S3 static asset).
 */
export function downloadOnboardingTemplate() {
  const link = document.createElement('a');
  link.href = ONBOARDING_TEMPLATE_URL;
  link.setAttribute('download', 'bulk_onboarding_template.csv');
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * uploads the raw CSV for the chosen target agency and returns the backend
 * payload { ok, fileError, results: [...] }. `agency` and `typeOfForm` travel
 * as query params (not multipart fields) so the backend's ForbiddenContentMiddleware
 * can authorize the destination agency before multer runs.
 * @param targetAgencyId - id of the agency the invites are sent to
 * @param typeOfForm - onboarding form type (defaults to 'downlineAgent')
 * @param file - the raw CSV File to upload
 */
export async function uploadBulkOnboarding({
  targetAgencyId,
  typeOfForm = 'downlineAgent',
  file
}) {
  const formData = new FormData();
  formData.append('file', file);

  const query = `?agency=${encodeURIComponent(
    targetAgencyId ?? ''
  )}&typeOfForm=${encodeURIComponent(typeOfForm)}`;

  const res = await safeAWSCallPost(
    `${endpoints.aws.bulkOnboardingInvite}${query}`,
    formData
  );

  if (!res?.data) {
    throw new Error('The upload failed. Please try again later.');
  }

  return res.data;
}

function csvEscape(value) {
  const s = String(value ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * builds and downloads `error_log.csv` from the per-row results — one row per
 * failed record, plus an Error_Message column describing why it failed.
 * @param results - per-row backend results to filter and export
 */
export function downloadErrorLog(results) {
  const failed = (results || []).filter(r => catOf(r) === 'error');
  if (failed.length === 0) return;

  const header = ['First_Name', 'Last_Name', 'NPN', 'Email', 'Error_Message'];
  const rows = failed.map(r => [
    r.firstName ?? '',
    r.lastName ?? '',
    r.npn ?? '',
    r.email ?? '',
    r.message ?? ''
  ]);

  const csv = [header, ...rows]
    .map(cols => cols.map(csvEscape).join(','))
    .join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'error_log.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
